// createDocumentFromSource.js
//
// Copy a source business document into a new target document (header + line items).

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import {
  normalizeAncestryPath,
  stampExclusiveAncestry,
} from 'utilities/nodeAncestry';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getResolvedFields } from 'utilities/NodeType';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { listAdjacentNodeKeys, upsertEdge } from 'services/nodes/NodeEdgeService';
import { getNode, getNodesByKeys } from 'services/nodes/NodeReadService';
import { createNode, setNodeDocs } from 'services/nodes/NodeWriteService';
import { DOCUMENT_TYPE_KEYS, normalizeDocumentTypeKey } from 'nodetypes/documents/create/documentCreateRegistry';
import { DOCUMENT_PAYMENT_TYPE_KEY } from 'nodetypes/documents/payments/documentPaymentKeys';
import { readDocumentPaymentField } from 'nodetypes/documents/payments/documentPaymentsModel';
import { computePurchaseOrderItemLineTotal } from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';

const PROJECT_TYPE_KEY = 'PROJECT';

const DOCUMENT_PAYMENT_COPY_FIELDS = Object.freeze([
  'amount',
  'amountBasis',
  'percentOfTotal',
  'paymentTerms',
  'dueDate',
  'status',
  'method',
  'reference',
  'paidDate',
  'source',
  'sortOrder',
  'notes',
  'feeCents',
  'appliedFromKey',
]);

const HEADER_COPY_FIELDS_BY_PAIR = Object.freeze({
  [`${DOCUMENT_TYPE_KEYS.PROPOSAL}->${DOCUMENT_TYPE_KEYS.PURCHASE_ORDER}`]: Object.freeze([
    'termsAndConditions',
    'memo',
    'paymentTerms',
  ]),
  [`${DOCUMENT_TYPE_KEYS.PROPOSAL}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'termsAndConditions',
    'memo',
    'paymentTerms',
    'client',
  ]),
  [`${DOCUMENT_TYPE_KEYS.PURCHASE_ORDER}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'termsAndConditions',
    'memo',
    'paymentTerms',
  ]),
  [`${DOCUMENT_TYPE_KEYS.INVOICE}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'termsAndConditions',
    'memo',
    'paymentTerms',
    'client',
  ]),
});

const LINE_COPY_FIELDS = Object.freeze([
  'sku',
  'manufacturer',
  'vendor',
  'unit',
  'leadTime',
  'product',
  'quantity',
  'description',
  'status',
  'sortOrder',
]);

/** Extra line fields copied for same-shape client docs (Proposal ↔ Invoice). */
const EXTRA_LINE_COPY_FIELDS_BY_PAIR = Object.freeze({
  [`${DOCUMENT_TYPE_KEYS.PROPOSAL}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'cost',
    'msrp',
    'markupPercent',
    'marginPercent',
    'unitPrice',
    'taxable',
    'color',
    'dimensions',
    'shipping',
    'shippingMarkupPercent',
    'room',
    'itemType',
    'category',
    'website',
  ]),
  [`${DOCUMENT_TYPE_KEYS.INVOICE}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'cost',
    'msrp',
    'markupPercent',
    'marginPercent',
    'unitPrice',
    'taxable',
    'color',
    'dimensions',
    'shipping',
    'shippingMarkupPercent',
    'room',
    'itemType',
    'category',
    'website',
  ]),
  [`${DOCUMENT_TYPE_KEYS.PURCHASE_ORDER}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze([
    'unitPrice',
    'category',
  ]),
});

/** Per-pair line field remaps: sourceField → targetField */
const LINE_FIELD_REMAP_BY_PAIR = Object.freeze({
  [`${DOCUMENT_TYPE_KEYS.PROPOSAL}->${DOCUMENT_TYPE_KEYS.PURCHASE_ORDER}`]: Object.freeze({
    cost: 'unitPrice',
  }),
  // PO buy price seeds invoice cost; sell starts equal until markup is applied.
  [`${DOCUMENT_TYPE_KEYS.PURCHASE_ORDER}->${DOCUMENT_TYPE_KEYS.INVOICE}`]: Object.freeze({
    unitPrice: 'cost',
  }),
});

const ITEM_TYPE_BY_DOCUMENT = Object.freeze({
  [DOCUMENT_TYPE_KEYS.PROPOSAL]: 'PROPOSAL_ITEM',
  [DOCUMENT_TYPE_KEYS.PURCHASE_ORDER]: 'PURCHASE_ORDER_ITEM',
  [DOCUMENT_TYPE_KEYS.INVOICE]: 'INVOICE_ITEM',
});

function normalizeString(value) {
  return String(value || '').trim();
}

function readField(node, fieldName, typeKey) {
  return readNodeFieldRawValue(
    node,
    { name: fieldName, source: String(typeKey || '').toLowerCase() },
    typeKey,
  );
}

function resolveSectionKey(typeDef, fieldName, typeKey) {
  const fields = [
    ...(Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : []),
    ...(getResolvedFields(typeKey, { includeHidden: true }) || []),
  ];
  const fieldDef = fields.find((field) => normalizeString(field?.name) === fieldName);
  const source = normalizeString(fieldDef?.source || typeDef?.key || typeKey).toLowerCase();
  if (typeDef?.dataSection && (!fieldDef?.source || fieldDef.source === typeDef.key)) {
    return typeDef.dataSection;
  }
  return source ? `data_${source}` : `data_${String(typeKey || '').toLowerCase()}`;
}

function applyFieldValue(node, typeDef, typeKey, fieldName, value) {
  const sectionKey = resolveSectionKey(typeDef, fieldName, typeKey);
  const section = node[sectionKey] && typeof node[sectionKey] === 'object'
    ? { ...node[sectionKey] }
    : {};
  section[fieldName] = value;
  node[sectionKey] = section;
}

/**
 * Resolve the Project a source document belongs to (ancestry first, then edges).
 *
 * @param {object} sourceNode
 * @param {string|null} baseKey
 * @returns {Promise<object|null>}
 */
async function resolveProjectParent(sourceNode, baseKey = null) {
  if (!sourceNode?.key) return null;

  const path = normalizeAncestryPath(sourceNode.ancestryPath);
  const fromPath = [...path].reverse().find((entry) => entry.type === PROJECT_TYPE_KEY);
  if (fromPath?.key) {
    try {
      const project = await getNode(fromPath.key);
      if (
        project?.key
        && normalizeDocumentTypeKey(project.type) === PROJECT_TYPE_KEY
      ) {
        return project;
      }
    } catch (error) {
      console.warn('[createDocumentFromSource] ancestry project load failed', error);
    }
  }

  try {
    const adjacentKeys = await listAdjacentNodeKeys(sourceNode.key, {
      baseKey: baseKey || sourceNode.baseKey || null,
    });
    if (!adjacentKeys.length) return null;
    const neighbors = await getNodesByKeys(adjacentKeys);
    return (Array.isArray(neighbors) ? neighbors : []).find(
      (row) => normalizeDocumentTypeKey(row?.type) === PROJECT_TYPE_KEY,
    ) || null;
  } catch (error) {
    console.warn('[createDocumentFromSource] adjacent project resolve failed', error);
    return null;
  }
}

/**
 * Create a target document from a source document.
 *
 * @param {object} options
 * @param {object} options.sourceNode
 * @param {string} options.targetTypeKey
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object[]} [options.sourceLineNodes]
 * @param {boolean} [options.includePayments=true] - When false, skip DOCUMENT_PAYMENT copy (Void & Duplicate)
 * @returns {Promise<object>} created target header node
 */
export async function createDocumentFromSource({
  sourceNode,
  targetTypeKey,
  user,
  baseKey = null,
  sourceLineNodes = [],
  includePayments = true,
}) {
  const sourceTypeKey = normalizeDocumentTypeKey(sourceNode?.type);
  const resolvedTargetTypeKey = normalizeDocumentTypeKey(targetTypeKey);
  if (!sourceNode?.key || !sourceTypeKey || !resolvedTargetTypeKey) {
    throw new Error('Source node and target type are required.');
  }

  const resolvedBaseKey = normalizeString(baseKey || sourceNode.baseKey);
  const targetType = getNodeType(resolvedTargetTypeKey);
  const pairKey = `${sourceTypeKey}->${resolvedTargetTypeKey}`;
  const headerFields = HEADER_COPY_FIELDS_BY_PAIR[pairKey] || [];

  const targetNode = await createNodeFromFactory(
    resolvedTargetTypeKey,
    user,
    resolvedBaseKey || null,
  );

  const title = normalizeString(sourceNode.title);
  if (title) {
    targetNode.title = title;
  }

  headerFields.forEach((fieldName) => {
    const value = readField(sourceNode, fieldName, sourceTypeKey);
    if (value === undefined) return;
    applyFieldValue(targetNode, targetType, resolvedTargetTypeKey, fieldName, value);
  });

  // Client on proposal → shipTo on PO when present
  if (
    sourceTypeKey === DOCUMENT_TYPE_KEYS.PROPOSAL
    && resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.PURCHASE_ORDER
  ) {
    const client = readField(sourceNode, 'client', sourceTypeKey);
    if (client !== undefined) {
      applyFieldValue(targetNode, targetType, resolvedTargetTypeKey, 'shipTo', client);
    }
  }

  // Ship-to on PO → client on Invoice when present
  if (
    sourceTypeKey === DOCUMENT_TYPE_KEYS.PURCHASE_ORDER
    && resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
  ) {
    const shipTo = readField(sourceNode, 'shipTo', sourceTypeKey);
    if (shipTo !== undefined) {
      applyFieldValue(targetNode, targetType, resolvedTargetTypeKey, 'client', shipTo);
    }
  }

  applyFieldValue(
    targetNode,
    targetType,
    resolvedTargetTypeKey,
    'derivedFrom',
    [sourceNode.key],
  );

  // New invoices always start as editable drafts (Issue lock is explicit).
  if (resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.INVOICE) {
    applyFieldValue(targetNode, targetType, resolvedTargetTypeKey, 'documentStatus', 'draft');
  }

  // Attach to the same Project as the source so Project related tabs see it.
  const projectNode = await resolveProjectParent(sourceNode, resolvedBaseKey);
  let headerToPersist = targetNode;
  if (projectNode?.key) {
    headerToPersist = {
      ...targetNode,
      parentTitle: projectNode.title || targetNode.parentTitle || null,
      parentType: projectNode.type || targetNode.parentType || null,
    };
    headerToPersist = stampExclusiveAncestry(
      headerToPersist,
      projectNode,
      targetType,
    );
  }

  await createNode(headerToPersist.key, headerToPersist);

  if (projectNode?.key) {
    await upsertEdge(
      projectNode.key,
      headerToPersist.key,
      buildHierarchyEdgeOptions({
        parentNode: projectNode,
        childNode: headerToPersist,
        baseKey: resolvedBaseKey || null,
      }),
    );
  }

  // Use stamped header for subsequent item ancestry / return value.
  const targetNodeForChildren = headerToPersist;

  const sourceItemType = ITEM_TYPE_BY_DOCUMENT[sourceTypeKey];
  const targetItemTypeKey = ITEM_TYPE_BY_DOCUMENT[resolvedTargetTypeKey];
  const targetItemType = targetItemTypeKey ? getNodeType(targetItemTypeKey) : null;

  const lines = (Array.isArray(sourceLineNodes) ? sourceLineNodes : [])
    .filter((row) => normalizeDocumentTypeKey(row?.type) === sourceItemType);

  for (const sourceLine of lines) {
    if (!targetItemTypeKey || !targetItemType) break;

    const itemNode = await createNodeFromFactory(
      targetItemTypeKey,
      user,
      resolvedBaseKey || null,
    );

    const lineTitle = normalizeString(sourceLine.title);
    if (lineTitle) itemNode.title = lineTitle;

    LINE_COPY_FIELDS.forEach((fieldName) => {
      const value = readField(sourceLine, fieldName, sourceItemType);
      if (value === undefined) return;
      applyFieldValue(itemNode, targetItemType, targetItemTypeKey, fieldName, value);
    });

    const extraLineFields = EXTRA_LINE_COPY_FIELDS_BY_PAIR[pairKey] || [];
    extraLineFields.forEach((fieldName) => {
      const value = readField(sourceLine, fieldName, sourceItemType);
      if (value === undefined) return;
      applyFieldValue(itemNode, targetItemType, targetItemTypeKey, fieldName, value);
    });

    const remaps = LINE_FIELD_REMAP_BY_PAIR[pairKey] || {};
    Object.entries(remaps).forEach(([sourceField, targetField]) => {
      const value = readField(sourceLine, sourceField, sourceItemType);
      if (value === undefined) return;
      applyFieldValue(itemNode, targetItemType, targetItemTypeKey, targetField, value);
    });

    // PO → Invoice: when buy price seeded cost, start sell equal (0% markup).
    if (
      sourceTypeKey === DOCUMENT_TYPE_KEYS.PURCHASE_ORDER
      && resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
    ) {
      const cost = readField(itemNode, 'cost', targetItemTypeKey);
      const existingUnitPrice = readField(itemNode, 'unitPrice', targetItemTypeKey);
      if (
        cost !== undefined
        && cost !== null
        && cost !== ''
        && (existingUnitPrice === undefined || existingUnitPrice === null || existingUnitPrice === '')
      ) {
        applyFieldValue(itemNode, targetItemType, targetItemTypeKey, 'unitPrice', cost);
      }
      if (readField(itemNode, 'markupPercent', targetItemTypeKey) === undefined) {
        applyFieldValue(itemNode, targetItemType, targetItemTypeKey, 'markupPercent', 0);
      }
    }

    const qty = readField(itemNode, 'quantity', targetItemTypeKey);
    const unitPrice = readField(itemNode, 'unitPrice', targetItemTypeKey);
    const lineTotal = computePurchaseOrderItemLineTotal(qty, unitPrice);
    if (lineTotal != null) {
      applyFieldValue(itemNode, targetItemType, targetItemTypeKey, 'total', lineTotal);
    }

    const stampedItem = stampExclusiveAncestry(itemNode, targetNodeForChildren, targetItemType);
    await setNodeDocs([{ key: stampedItem.key, payload: stampedItem }], { merge: false });
    await upsertEdge(
      targetNodeForChildren.key,
      stampedItem.key,
      buildHierarchyEdgeOptions({
        parentNode: targetNodeForChildren,
        childNode: stampedItem,
        baseKey: resolvedBaseKey || null,
      }),
    );
  }

  // Proposal/Invoice → Invoice: carry DOCUMENT_PAYMENT schedule + paid history.
  // Void & Duplicate passes includePayments: false (fresh draft, no ledger).
  const canCopyPayments = (
    includePayments
    && resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
    && (
      sourceTypeKey === DOCUMENT_TYPE_KEYS.PROPOSAL
      || sourceTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
    )
    // Same-type duplicate should not copy paid history into a new draft.
    && !(
      sourceTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
      && resolvedTargetTypeKey === DOCUMENT_TYPE_KEYS.INVOICE
    )
  );
  if (canCopyPayments) {
    const paymentType = getNodeType(DOCUMENT_PAYMENT_TYPE_KEY);
    const sourcePayments = (Array.isArray(sourceLineNodes) ? sourceLineNodes : [])
      .filter((row) => normalizeDocumentTypeKey(row?.type) === DOCUMENT_PAYMENT_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readDocumentPaymentField(left, 'sortOrder'));
        const rightOrder = Number(readDocumentPaymentField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return normalizeString(left?.title).localeCompare(normalizeString(right?.title));
      });

    for (const sourcePayment of sourcePayments) {
      if (!paymentType) break;

      const paymentNode = await createNodeFromFactory(
        DOCUMENT_PAYMENT_TYPE_KEY,
        user,
        resolvedBaseKey || null,
      );

      const paymentTitle = normalizeString(sourcePayment.title);
      if (paymentTitle) paymentNode.title = paymentTitle;

      DOCUMENT_PAYMENT_COPY_FIELDS.forEach((fieldName) => {
        const value = readDocumentPaymentField(sourcePayment, fieldName);
        if (value === undefined || value === null || value === '') return;
        applyFieldValue(
          paymentNode,
          paymentType,
          DOCUMENT_PAYMENT_TYPE_KEY,
          fieldName,
          value,
        );
      });

      const stampedPayment = stampExclusiveAncestry(paymentNode, targetNodeForChildren, paymentType);
      await setNodeDocs([{ key: stampedPayment.key, payload: stampedPayment }], { merge: false });
      await upsertEdge(
        targetNodeForChildren.key,
        stampedPayment.key,
        buildHierarchyEdgeOptions({
          parentNode: targetNodeForChildren,
          childNode: stampedPayment,
          baseKey: resolvedBaseKey || null,
        }),
      );
    }
  }

  return targetNodeForChildren;
}
